import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  FiArrowLeft,
  FiRefreshCcw,
  FiCalendar,
  FiUsers,
  FiBookOpen,
  FiClock,
  FiPlayCircle,
  FiCheckCircle,
} from 'react-icons/fi'
import {
  fetchBatchDetail as fetchBatchDetailApi,
  startLecture as startLectureApi,
  endLecture as endLectureApi,
} from '../../api/staffTeachingApi'
import './BatchDetail.css'

const formatDate = (value) => {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const formatTimeRange = (start, end) => {
  if (!start) {
    return {
      pending: true,
    }
  }
  const startDate = new Date(start)
  const endDate = end ? new Date(end) : null
  const dayLabel = startDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
  const startLabel = startDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })
  const duration = endDate ? `${Math.max(1, Math.round((endDate - startDate) / 60000))} min` : '—'
  return {
    day: dayLabel,
    startLabel,
    duration,
  }
}

const titleCase = (value) => (value ? value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : '—')

const BatchDetail = () => {
  const { batchId } = useParams()
  const navigate = useNavigate()
  const [detail, setDetail] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lectureStatus, setLectureStatus] = useState({})
  const [studentQuery, setStudentQuery] = useState('')

  const loadBatchDetail = useCallback(
    async (options = {}) => {
      if (!batchId) return
      if (!options.silent) {
        setIsLoading(true)
        setError(null)
      }

      try {
        const data = await fetchBatchDetailApi(batchId)
        setDetail(data)
      } catch (apiError) {
        console.error('Failed to fetch batch detail', apiError)
        setError(apiError?.response?.data?.message || apiError.message || 'Unable to load batch')
      } finally {
        if (!options.silent) {
          setIsLoading(false)
        }
      }
    },
    [batchId]
  )

  useEffect(() => {
    loadBatchDetail()
  }, [loadBatchDetail])

  const stats = useMemo(() => {
    if (!detail?.stats) {
      return [
        { label: 'Enrolled students', value: '—', icon: FiUsers },
        { label: 'Modules', value: '—', icon: FiBookOpen },
        { label: 'Upcoming lectures', value: '—', icon: FiCalendar },
        { label: 'Recent lectures', value: '—', icon: FiClock },
      ]
    }

    return [
      { label: 'Enrolled students', value: detail.stats.students, icon: FiUsers },
      { label: 'Modules', value: detail.stats.modules, icon: FiBookOpen },
      { label: 'Upcoming lectures', value: detail.stats.upcomingLectures, icon: FiCalendar },
      { label: 'Recent lectures', value: detail.stats.recentLectures, icon: FiClock },
    ]
  }, [detail])

  const modules = detail?.modules || []
  const students = detail?.students || []

  const filteredStudents = useMemo(() => {
    if (!students.length) return []
    const value = studentQuery.trim().toLowerCase()
    if (!value) return students
    return students.filter((enrollment) => {
      const student = enrollment.user || enrollment.student || enrollment.learner
      const tokens = [student?.firstName, student?.lastName, student?.email, enrollment.enrollmentId]
      return tokens.filter(Boolean).some((token) => token.toLowerCase().includes(value))
    })
  }, [students, studentQuery])

  const setLectureMutationState = (lectureId, state) => {
    setLectureStatus((prev) => ({ ...prev, [lectureId]: state }))
  }

  const handleStartLecture = async (lectureId) => {
    try {
      setLectureMutationState(lectureId, 'starting')
      setError(null)
      await startLectureApi(lectureId)
      await loadBatchDetail({ silent: true })
    } catch (apiError) {
      console.error('Failed to start lecture', apiError)
      setError(apiError?.response?.data?.message || apiError.message || 'Unable to start lecture')
    } finally {
      setLectureMutationState(lectureId, null)
    }
  }

  const handleEndLecture = async (lectureId) => {
    try {
      setLectureMutationState(lectureId, 'ending')
      setError(null)
      await endLectureApi(lectureId)
      await loadBatchDetail({ silent: true })
    } catch (apiError) {
      console.error('Failed to end lecture', apiError)
      setError(apiError?.response?.data?.message || apiError.message || 'Unable to end lecture')
    } finally {
      setLectureMutationState(lectureId, null)
    }
  }

  if (!batchId) {
    return (
      <div className="batch-detail">
        <div className="batch-detail__empty">
          <p>Batch identifier missing. Navigate back to batches.</p>
          <button type="button" onClick={() => navigate('/batches')} className="outline-button">
            <FiArrowLeft size={16} /> Back to batches
          </button>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="batch-detail">
        <div className="batch-detail__state">Loading batch insights…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="batch-detail">
        <div className="batch-detail__error">
          <p>{error}</p>
          <button type="button" onClick={() => loadBatchDetail()}>
            <FiRefreshCcw size={16} /> Retry
          </button>
        </div>
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="batch-detail">
        <div className="batch-detail__state">Batch details unavailable.</div>
      </div>
    )
  }

  const { batch = {}, lectures = {} } = detail
  const upcomingLectures = lectures.upcoming || []
  const recentLectures = lectures.recent || []

  return (
    <div className="batch-detail">
      <header className="batch-detail__header">
        <div>
          <button type="button" className="inline-back" onClick={() => navigate('/batches')}>
            <FiArrowLeft size={16} /> Back to batches
          </button>
          <h1>{batch.batchName || 'Unnamed batch'}</h1>
          <p>
            {batch.course?.title || 'Programme'} · {titleCase(batch.status)} · {formatDate(batch.startDate)} –{' '}
            {formatDate(batch.endDate)}
          </p>
        </div>
        <button type="button" className="refresh-button" onClick={() => loadBatchDetail()}>
          <FiRefreshCcw size={16} /> Refresh data
        </button>
      </header>

      <section className="batch-detail__stats">
        {stats.map((metric) => {
          const Icon = metric.icon
          return (
            <article key={metric.label} className="batch-detail__stat-card">
              <span className="stat-icon">
                <Icon size={18} />
              </span>
              <div>
                <p>{metric.label}</p>
                <strong>{metric.value}</strong>
              </div>
            </article>
          )
        })}
      </section>

      <section className="batch-detail__content">
        <div className="batch-detail__column">
          <article className="batch-panel">
            <header>
              <div>
                <h2>Upcoming lectures</h2>
                <p>Stay ahead of your scheduled sessions</p>
              </div>
              <Link to="/lectures/new" className="text-link">
                Schedule lecture
              </Link>
            </header>
            {upcomingLectures.length ? (
              <ul className="lecture-list">
                {upcomingLectures.map((lecture) => {
                  const timing = formatTimeRange(lecture.startTime, lecture.endTime)
                  const mutationState = lectureStatus[lecture._id]
                  const canStart = ['scheduled', 'pending'].includes(lecture.status)
                  const startDisabled = mutationState === 'starting' || !canStart
                  return (
                    <li key={lecture._id} className="lecture-card">
                      <div className="lecture-card__meta">
                        <strong>{lecture.topic}</strong>
                        <span>{lecture.module?.title || 'Module TBD'}</span>
                        <span className={`status-pill status-${lecture.status}`}>{titleCase(lecture.status)}</span>
                      </div>
                      <div className="lecture-card__schedule">
                        {timing.pending ? (
                          <span className="pending">Pending scheduling</span>
                        ) : (
                          <>
                            <span>{timing.day}</span>
                            <span>{timing.startLabel}</span>
                            <span>{timing.duration}</span>
                          </>
                        )}
                      </div>
                      <div className="lecture-card__actions">
                        <button
                          type="button"
                          onClick={() => handleStartLecture(lecture._id)}
                          disabled={startDisabled}
                        >
                          <FiPlayCircle size={16} />{' '}
                          {mutationState === 'starting' ? 'Starting…' : canStart ? 'Start' : 'Unavailable'}
                        </button>
                        <Link to={`/lectures/${lecture._id}`}>Details</Link>
                      </div>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className="panel-state">No upcoming lectures scheduled. Plan the next session.</div>
            )}
          </article>

          <article className="batch-panel">
            <header>
              <div>
                <h2>Recent lectures</h2>
                <p>Review delivery and attendance insights</p>
              </div>
            </header>
            {recentLectures.length ? (
              <ul className="lecture-list">
                {recentLectures.map((lecture) => {
                  const timing = formatTimeRange(lecture.startTime, lecture.endTime)
                  const mutationState = lectureStatus[lecture._id]
                  const canEnd = ['in_progress', 'ongoing'].includes(lecture.status)
                  const endDisabled = mutationState === 'ending' || !canEnd
                  return (
                    <li key={lecture._id} className="lecture-card">
                      <div className="lecture-card__meta">
                        <strong>{lecture.topic}</strong>
                        <span>{lecture.module?.title || 'Module TBD'}</span>
                        <span className={`status-pill status-${lecture.status}`}>{titleCase(lecture.status)}</span>
                      </div>
                      <div className="lecture-card__schedule">
                        {timing.pending ? (
                          <span className="pending">Pending scheduling</span>
                        ) : (
                          <>
                            <span>{timing.day}</span>
                            <span>{timing.startLabel}</span>
                            <span>{timing.duration}</span>
                          </>
                        )}
                      </div>
                      <div className="lecture-card__actions">
                        <button
                          type="button"
                          onClick={() => handleEndLecture(lecture._id)}
                          disabled={endDisabled}
                        >
                          <FiCheckCircle size={16} />{' '}
                          {mutationState === 'ending' ? 'Wrapping…' : canEnd ? 'Mark complete' : 'Completed'}
                        </button>
                        <Link to={`/lectures/${lecture._id}`}>Attendance</Link>
                      </div>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className="panel-state">No recent deliveries logged.</div>
            )}
          </article>
        </div>

        <aside className="batch-detail__aside">
          <article className="batch-panel">
            <header>
              <h2>Module roadmap</h2>
            </header>
            {modules.length ? (
              <ul className="module-list">
                {modules.map((module, index) => {
                  const sequence = module.sequence ?? module.order ?? index + 1
                  const isActive = ['active', 'in_progress', 'ongoing'].includes(module.status)
                  const statusLabel = module.totalLectures
                    ? `${module.totalLectures} lectures`
                    : module.status
                    ? titleCase(module.status)
                    : 'Status TBD'
                  return (
                    <li key={module._id || sequence} className={isActive ? 'is-active' : ''}>
                      <div>
                        <strong>Module {sequence}</strong>
                        <span>{module.title || 'Untitled module'}</span>
                      </div>
                      <span>{statusLabel}</span>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className="panel-state">No modules have been assigned to this batch yet.</div>
            )}
          </article>

          <article className="batch-panel">
            <header>
              <h2>Students</h2>
            </header>
            <label className="search-field">
              <input
                type="search"
                placeholder="Search learners by name or email"
                value={studentQuery}
                onChange={(event) => setStudentQuery(event.target.value)}
              />
            </label>
            {filteredStudents.length ? (
              <ul className="student-list">
                {filteredStudents.map((enrollment) => {
                  const student = enrollment.user || enrollment.student || enrollment.learner || {}
                  const studentLink = student._id || student.id || enrollment.studentId || enrollment.userId
                  return (
                    <li key={enrollment._id || student._id || student.id}>
                    <div className="student-meta">
                        <span>
                          {student.firstName} {student.lastName}
                        </span>
                        <small>{student.email || 'No email available'}</small>
                    </div>
                      {studentLink ? (
                        <Link to={`/students/${studentLink}`} className="text-link">
                          View
                        </Link>
                      ) : (
                        <span className="text-link disabled">No profile</span>
                      )}
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className="panel-state">No students match that search.</div>
            )}
          </article>
        </aside>
      </section>
    </div>
  )
}

export default BatchDetail
